import {FilterIcon, SearchIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, TextInput} from '@primer/react'
import {clsx} from 'clsx'

import type {Author, Thread} from '@github-ui/diff-lines/conversation-types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import {fileLevelCommentKey, type LineKey} from '@github-ui/diff-lines/types'
import {useGetThread} from '../../../stores/selectors'
import {useMemo} from 'react'
import type {IssueComment} from '../../../page-data/payloads/issue-comments'
import {FeatureFlags} from '@primer/react/experimental'

/**
 * Data on each thread that is used to determine if it matches the current filter
 */
type ThreadFilterData = {
  authorLogin: string
  body: string
  createdAt: string
  id: string
  isOutdated: boolean
  isResolved: boolean
  path: string
  pullRequestReviewId?: number | null
}

export type AuthorFilterItem = Pick<Author, 'login' | 'avatarUrl'>

/**
 * Return a set of thread ids that match the current filter state
 */
export function filterComments(
  threadPreviews: Thread[],
  filterState: CommentsFilterState,
  getThread: ReturnType<typeof useGetThread>,
): Set<string> {
  const parsedFilterKeywords = filterState.filterText ? parseFilterKeywords(filterState.filterText) : undefined

  const matchingThreadIds = threadPreviews.filter(thread => {
    const firstComment = getFirstComment(thread)
    const filePath = thread.path ?? ''

    // Get the line key for looking up the thread in the store
    const isRightOrLeftSide = thread.subject?.endDiffSide === 'RIGHT' ? 'R' : 'L'
    const lineKey = thread.subject?.originalEndLine
      ? (`${isRightOrLeftSide}${thread.subject.originalEndLine}` as LineKey)
      : fileLevelCommentKey

    // Fetch the actual thread from the store
    const storedThread = getThread(Number(thread.id), filePath, lineKey)

    const threadFilterData: ThreadFilterData = {
      authorLogin: firstComment?.author?.login ?? '',
      body: firstComment?.body ?? '',
      createdAt: firstComment?.createdAt ?? '',
      id: thread.id,
      isResolved: storedThread?.isResolved ?? thread.isResolved ?? false,
      isOutdated: (thread.isOutdated || thread.subject?.isOutdated || thread.positioning?.isOutdated) ?? false,
      path: filePath,
      pullRequestReviewId: thread.pullRequestReviewId,
    }

    return filterThread(threadFilterData, filterState, parsedFilterKeywords)
  })

  return new Set([...matchingThreadIds.map(thread => thread.id ?? '')])
}

/**
 * Hook that filters thread previews and returns a set of matching thread IDs.
 */
export function useFilteredThreadIds(threadPreviews: Thread[], filterState: CommentsFilterState): Set<string> {
  const getThread = useGetThread()

  return useMemo(() => filterComments(threadPreviews, filterState, getThread), [threadPreviews, filterState, getThread])
}

function filterThread(
  threadData: ThreadFilterData,
  filterState: CommentsFilterState,
  parsedFilterKeywords?: ReturnType<typeof parseFilterKeywords>,
) {
  const {filterText, hideResolvedThreads, hideOutdatedThreads} = filterState

  if ((hideResolvedThreads && threadData.isResolved) || (hideOutdatedThreads && threadData.isOutdated)) {
    return false
  }

  if (filterText) {
    const {afterDate, reviewId, remainingText} = parsedFilterKeywords ?? parseFilterKeywords(filterText)

    if (afterDate && !isAfterDate(threadData.createdAt, afterDate)) {
      return false
    }

    if (reviewId !== null && threadData.pullRequestReviewId !== reviewId) {
      return false
    }

    if (remainingText) {
      const filterTextLower = remainingText.toLowerCase()
      if (
        !threadData.authorLogin.toLowerCase().includes(filterTextLower) &&
        !threadData.body.toLowerCase().includes(filterTextLower) &&
        !threadData.path.toLowerCase().includes(filterTextLower)
      ) {
        return false
      }
    }
  }

  if (filterState.selectedAuthor && threadData.authorLogin !== filterState.selectedAuthor) {
    return false
  }

  return true
}

/**
 * Parses the below keywords from filter text.
 * -  `after:YYYY-MM-DD` to filter comments created after a specific date.
 * -  `review-id:NUMBER` to filter comments by a specific review ID.
 *
 * Returns parsed keywords and any remaining text for free-text matching.
 */
export function parseFilterKeywords(filterText: string): {
  afterDate: Date | null
  reviewId: number | null
  remainingText: string
} {
  const afterMatch = filterText.match(/(^|\s)after:(\S+)/i)
  let afterDate: Date | null = null
  let reviewId: number | null = null

  const reviewIdMatch = filterText.match(/(^|\s)review-id:(\d+)(?=\s|$)/i)
  if (reviewIdMatch?.[2]) {
    reviewId = parseInt(reviewIdMatch[2], 10)
  }

  if (afterMatch?.[2]) {
    const parsed = new Date(afterMatch[2])
    if (!isNaN(parsed.getTime())) {
      afterDate = parsed
    }
  }

  const remainingText = filterText
    .replace(/(^|\s)after:\S*/gi, '$1')
    .replace(/(^|\s)review-id:\S*/gi, '$1')
    .trim()

  return {afterDate, reviewId, remainingText}
}

function isAfterDate(createdAt: string, afterDate: Date): boolean {
  const commentDate = new Date(createdAt)
  return !isNaN(commentDate.getTime()) && commentDate > afterDate
}

export function useFilterIssueCommentsIds(
  issueComments: IssueComment[],
  filterState: CommentsFilterState,
): Set<string> {
  const matchingCommentIds = useMemo(
    () =>
      issueComments.filter(comment => {
        const commentFilterData: ThreadFilterData = {
          authorLogin: comment.authorLogin,
          body: comment.body,
          createdAt: comment.createdAt,
          id: comment.id,
          isResolved: false, // issue comments don't have resolved state
          isOutdated: false, // issue comments don't have outdated state
          path: '', // issue comments don't have a file path
        }
        return filterThread(commentFilterData, filterState)
      }),
    [issueComments, filterState],
  )

  return new Set([...matchingCommentIds.map(comment => comment.id)])
}

/**
 * The state values of the comments filter
 */
export type CommentsFilterState = {
  filterText: string
  hideResolvedThreads: boolean
  hideOutdatedThreads: boolean
  selectedAuthor?: string
}

const defaultFilterState: CommentsFilterState = {
  filterText: '',
  hideResolvedThreads: true,
  hideOutdatedThreads: false,
}

export function getDefaultFilterState(): CommentsFilterState {
  return {...defaultFilterState}
}

type CommentsFilterProps = {
  authorList: AuthorFilterItem[]
  className?: string
  filterState: CommentsFilterState
  onFilterStateChange: (filterState: CommentsFilterState) => void
  hideReviewThreadFilters: boolean
  iconOnlyReviewCommentsFilter?: boolean
}

export function CommentsFilter({
  authorList,
  className,
  filterState,
  onFilterStateChange,
  hideReviewThreadFilters,
  iconOnlyReviewCommentsFilter = false,
}: CommentsFilterProps) {
  const updateFilterText = (filterText: string) => {
    onFilterStateChange({...filterState, filterText})
  }

  const toggleResolvedFilter = () => {
    onFilterStateChange({...filterState, hideResolvedThreads: !filterState.hideResolvedThreads})
  }

  const toggleOutdatedFilter = () => {
    onFilterStateChange({...filterState, hideOutdatedThreads: !filterState.hideOutdatedThreads})
  }

  const onSelectAuthor = (authorLogin: string) => {
    if (filterState.selectedAuthor === authorLogin) {
      return onFilterStateChange({...filterState, selectedAuthor: undefined})
    }

    onFilterStateChange({...filterState, selectedAuthor: authorLogin})
  }

  return (
    <div className={clsx('d-flex flex-row gap-2 flex-nowrap', className)}>
      <TextInput
        block
        aria-label="Filter comments"
        leadingVisual={SearchIcon}
        placeholder="Filter comments"
        value={filterState.filterText}
        onChange={event => updateFilterText(event.target.value)}
      />
      {!hideReviewThreadFilters || authorList.length > 1 ? (
        <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
          <ActionMenu>
            <ActionMenu.Anchor>
              {iconOnlyReviewCommentsFilter ? (
                <IconButton icon={FilterIcon} aria-label="Additional comment filters" />
              ) : (
                <Button
                  aria-label="Additional comment filters"
                  className="flex-shrink-0"
                  leadingVisual={FilterIcon}
                  trailingAction={TriangleDownIcon}
                >
                  Filter
                </Button>
              )}
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="small">
              <ActionList selectionVariant="single">
                {!hideReviewThreadFilters && (
                  <>
                    <ActionList.Item selected={filterState.hideResolvedThreads} onSelect={() => toggleResolvedFilter()}>
                      Hide resolved comments
                    </ActionList.Item>
                    <ActionList.Item selected={filterState.hideOutdatedThreads} onSelect={() => toggleOutdatedFilter()}>
                      Hide outdated comments
                    </ActionList.Item>
                  </>
                )}
                {authorList.length > 1 && (
                  <>
                    {!hideReviewThreadFilters && <ActionList.Divider />}
                    <ActionList.Group>
                      <ActionList.GroupHeading>Filter by</ActionList.GroupHeading>

                      {authorList.map(author => (
                        <ActionList.Item
                          key={author.login}
                          selected={filterState.selectedAuthor === author.login}
                          onSelect={() => onSelectAuthor(author.login)}
                        >
                          <ActionList.LeadingVisual>
                            <GitHubAvatar src={author.avatarUrl} />
                          </ActionList.LeadingVisual>
                          {author.login}
                        </ActionList.Item>
                      ))}
                    </ActionList.Group>
                  </>
                )}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </FeatureFlags>
      ) : null}
    </div>
  )
}
